// How We Do It -- visual editing INSIDE the editor's preview frame.
//
// The preview is a sandboxed iframe without allow-same-origin: the editor
// cannot touch its DOM. This script runs in the frame (editor preview only,
// never on the public page) and:
//   - draws a selection frame fitted to what the block visibly shows (text,
//     image...) -- no full-width band, no padding, no change to the page;
//   - selects the innermost block under the pointer (Alt+click: its parent);
//   - moves the selected block with the mouse (Shift: one axis) or the arrow
//     keys (1 px, Shift 10 px), resizes it with 8 handles (corner + Shift, or
//     images/videos by default: proportional);
//   - reports each finished change to the editor, which saves it in the
//     block's layout for the device being edited and re-renders the frame.
// Nothing here is trusted by the editor: it validates every id and number.

import { getDeviceLayout } from "./blockLayout";

export const DESIGNER_MESSAGE_KIND = "designer";

const SAFE_ID = /^[A-Za-z0-9_-]{1,120}$/;
const RATIO_TYPES = new Set(["image", "video", "gallery"]);

/** What the frame needs to know about each block, for one device. */
export const buildDesignerBlocks = (blocks = [], device = "desktop", typeLabel = (type) => type, unlockedIds = []) => {
  const out = {};
  const unlocked = new Set(Array.isArray(unlockedIds) ? unlockedIds : []);
  const visit = (block) => {
    if (!block || !SAFE_ID.test(String(block.blockId || ""))) return;
    const values = getDeviceLayout(block.settings?.layout, device);
    out[block.blockId] = {
      free: values.mode === "free",
      sized: !!(values.w || values.h),
      ratio: RATIO_TYPES.has(block.type),
      label: String(typeLabel(block.type) || block.type).slice(0, 40),
      locked: !unlocked.has(block.blockId),
    };
    (Array.isArray(block.children) ? block.children : []).forEach(visit);
  };
  (Array.isArray(blocks) ? blocks : []).forEach(visit);
  return out;
};

const DESIGNER_CSS = `
html.hwdi-dz{overflow-x:clip}
html.hwdi-dz:focus{outline:none}
#hwdi-dz-hover,#hwdi-dz-box{position:absolute;display:none;pointer-events:none;box-sizing:border-box;z-index:2147483646}
#hwdi-dz-hover{outline:1px dashed rgba(37,99,235,.75);outline-offset:0}
#hwdi-dz-box{outline:2px solid #2563eb;outline-offset:0;z-index:2147483647}
#hwdi-dz-box .hwdi-dz-h{position:absolute;width:10px;height:10px;margin:-5px 0 0 -5px;background:#fff;border:2px solid #2563eb;border-radius:3px;box-sizing:border-box;pointer-events:auto}
#hwdi-dz-box .hwdi-dz-h[data-dir=nw]{left:0;top:0;cursor:nwse-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=n]{left:50%;top:0;cursor:ns-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=ne]{left:100%;top:0;cursor:nesw-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=e]{left:100%;top:50%;cursor:ew-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=se]{left:100%;top:100%;cursor:nwse-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=s]{left:50%;top:100%;cursor:ns-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=sw]{left:0;top:100%;cursor:nesw-resize}
#hwdi-dz-box .hwdi-dz-h[data-dir=w]{left:0;top:50%;cursor:ew-resize}
#hwdi-dz-box[data-locked=true]{outline-color:#64748b}
#hwdi-dz-box[data-locked=true] .hwdi-dz-h{display:none}
#hwdi-dz-box[data-locked=true] #hwdi-dz-label{background:#64748b}
#hwdi-dz-label{position:absolute;left:-2px;bottom:100%;margin-bottom:4px;padding:2px 6px;border-radius:4px;background:#2563eb;color:#fff;font:600 11px/1.4 system-ui,-apple-system,"Segoe UI",sans-serif;white-space:nowrap}
html.hwdi-dz-edit [data-elakay-block-id]{cursor:move}
html.hwdi-dz-edit a,html.hwdi-dz-edit button,html.hwdi-dz-edit input,html.hwdi-dz-edit select,html.hwdi-dz-edit textarea,html.hwdi-dz-edit label{cursor:inherit}
html.hwdi-dz-dragging,html.hwdi-dz-dragging *{user-select:none!important;-webkit-user-select:none!important}
[data-hwdi-resizing]>.hwdi-block{width:100%;height:100%;max-width:none;box-sizing:border-box}
[data-hwdi-resizing]>.hwdi-image{display:flex;flex-direction:column}
[data-hwdi-resizing]>.hwdi-image img,[data-hwdi-resizing]>.hwdi-image a{flex:1 1 auto;min-height:0;width:100%!important;height:100%!important;object-fit:contain}
[data-hwdi-resizing]>.hwdi-video{display:flex;flex-direction:column}
[data-hwdi-resizing]>.hwdi-video>.hwdi-video-frame{flex:1 1 auto;min-height:0;padding-bottom:0!important;height:100%}
`;

const designerRuntime = function designerRuntime(C) {
  var root = document.documentElement;
  var selectedId = C.selectedId || null;
  var selected = null;
  var hoverBox, box, label;
  var nudgeTimer = null;
  var nudge = null;

  function post(type, data) {
    var payload = { type: type };
    for (var key in data || {}) payload[key] = data[key];
    try { window.parent.postMessage({ source: C.source, kind: C.kind, data: payload }, "*"); } catch (e) { /* ignore */ }
  }
  // Keyboard focus for the arrow keys, WITHOUT scrolling: window.focus()
  // makes the editor scroll the whole frame into view, which moved the page
  // under the pointer in the middle of a drag.
  function takeFocus() {
    try {
      if (!root.hasAttribute("tabindex")) root.setAttribute("tabindex", "-1");
      root.focus({ preventScroll: true });
    } catch (err) { /* ignore */ }
  }
  function idOf(el) { return el ? el.getAttribute("data-elakay-block-id") : null; }
  function info(el) { return (el && C.blocks[idOf(el)]) || {}; }
  function wrapOf(node) {
    while (node && node.nodeType !== 1) node = node.parentNode;
    return node && node.closest ? node.closest("[data-elakay-block-id]") : null;
  }
  function byId(id) {
    if (!id) return null;
    var all = document.querySelectorAll("[data-elakay-block-id]");
    for (var i = 0; i < all.length; i++) if (idOf(all[i]) === id) return all[i];
    return null;
  }
  function isOurs(node) { return !!(node && node.closest && node.closest("#hwdi-dz-box,#hwdi-dz-hover")); }

  var REPLACED = /^(IMG|VIDEO|IFRAME|SVG|CANVAS|INPUT|SELECT|TEXTAREA|BUTTON|HR|TABLE|PICTURE|OBJECT|EMBED|PROGRESS|METER)$/;
  function painted(cs) {
    var bg = cs.backgroundColor;
    var border = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderRightWidth) + parseFloat(cs.borderBottomWidth) + parseFloat(cs.borderLeftWidth);
    return (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") || cs.backgroundImage !== "none" || border > 0 || cs.boxShadow !== "none";
  }
  // What the block visibly shows: text lines, images and other replaced
  // elements, boxes with a background / border. A positioned or sized
  // block is selected with its real box.
  function visualRect(el) {
    var whole = el.getBoundingClientRect();
    var meta = info(el);
    if (meta.free || meta.sized || el.hasAttribute("data-hwdi-resizing")) return whole;
    var L = Infinity, T = Infinity, R = -Infinity, B = -Infinity, found = false;
    function add(r) {
      if (!r || (r.width <= 0 && r.height <= 0)) return;
      found = true;
      L = Math.min(L, r.left); T = Math.min(T, r.top); R = Math.max(R, r.right); B = Math.max(B, r.bottom);
    }
    var range = document.createRange();
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === 3) {
        if (!/\S/.test(node.nodeValue)) continue;
        range.selectNodeContents(node);
        add(range.getBoundingClientRect());
        continue;
      }
      var cs = getComputedStyle(node);
      if (cs.display === "none" || cs.visibility === "hidden") continue;
      if (REPLACED.test(node.tagName.toUpperCase()) || node.hasAttribute("data-hwdi-video-slot") || painted(cs)) add(node.getBoundingClientRect());
    }
    if (!found) return whole;
    return { left: L, top: T, right: R, bottom: B, width: R - L, height: B - T };
  }
  function place(target, r) {
    target.style.left = (r.left + window.scrollX) + "px";
    target.style.top = (r.top + window.scrollY) + "px";
    target.style.width = Math.max(1, r.width) + "px";
    target.style.height = Math.max(1, r.height) + "px";
    target.style.display = "block";
  }
  function refresh() {
    if (!box) return;
    if (selectedId && (!selected || !document.contains(selected))) selected = byId(selectedId);
    if (!selected) { box.style.display = "none"; return; }
    var r = visualRect(selected);
    box.setAttribute("data-locked", info(selected).locked ? "true" : "false");
    place(box, r);
    label.textContent = (info(selected).label || "") + "  " + Math.round(r.width) + " × " + Math.round(r.height);
  }
  function select(el, notify, reveal) {
    selected = el || null;
    selectedId = idOf(selected);
    if (hoverBox) hoverBox.style.display = "none";
    refresh();
    if (notify) post("select", { blockId: selectedId });
    if (reveal && selected) {
      try { window.parent.postMessage({ source: C.source, kind: "scrollTo", data: { y: visualRect(selected).top, reveal: true } }, "*"); } catch (e) { /* ignore */ }
    }
  }

  // Left / top as the page currently places the block (px), whatever its mode.
  function offsets(el) {
    var cs = getComputedStyle(el);
    if (cs.position === "static") return { x: 0, y: 0, positioned: false };
    return { x: parseFloat(cs.left) || 0, y: parseFloat(cs.top) || 0, positioned: true };
  }
  function container(el) {
    var parentWrap = wrapOf(el.parentElement);
    return parentWrap || el.closest(".hwdi-block-list") || document.body;
  }

  function startMove(e, el) {
    var startX = e.clientX, startY = e.clientY, moved = false;
    var start = offsets(el);
    var x = start.x, y = start.y;
    function move(ev) {
      var dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (!moved) {
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        moved = true;
        root.classList.add("hwdi-dz-dragging");
        if (!start.positioned) el.style.position = "relative";
      }
      if (ev.shiftKey) { if (Math.abs(dx) > Math.abs(dy)) dy = 0; else dx = 0; }
      x = Math.round(start.x + dx); y = Math.round(start.y + dy);
      el.style.left = x + "px";
      el.style.top = y + "px";
      refresh();
    }
    function end() {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);
      root.classList.remove("hwdi-dz-dragging");
      if (moved) post("change", { blockId: idOf(el), values: { x: x, y: y } });
    }
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", end, true);
    window.addEventListener("pointercancel", end, true);
  }

  function startResize(e, dir) {
    var el = selected;
    if (!el) return;
    var meta = info(el);
    var start = offsets(el);
    var whole = el.getBoundingClientRect();
    var seen = visualRect(el);
    var x0 = start.x, y0 = start.y, w0 = whole.width, h0 = whole.height;
    if (!meta.free && !meta.sized) {
      // First resize of a block in the flow: the frame starts on what the
      // block visibly shows, so nothing jumps.
      x0 += seen.left - whole.left; y0 += seen.top - whole.top; w0 = seen.width; h0 = seen.height;
    }
    var ratio = w0 / (h0 || 1);
    var startX = e.clientX, startY = e.clientY;
    var x = x0, y = y0, w = w0, h = h0;
    el.setAttribute("data-hwdi-resizing", "");
    if (!start.positioned) el.style.position = "relative";
    root.classList.add("hwdi-dz-dragging");
    function apply() {
      el.style.left = x + "px"; el.style.top = y + "px";
      el.style.width = w + "px"; el.style.height = h + "px";
      el.style.maxWidth = "none";
      refresh();
    }
    apply();
    function move(ev) {
      var dx = ev.clientX - startX, dy = ev.clientY - startY;
      w = w0; h = h0;
      if (dir.indexOf("e") !== -1) w = w0 + dx;
      if (dir.indexOf("w") !== -1) w = w0 - dx;
      if (dir.indexOf("s") !== -1) h = h0 + dy;
      if (dir.indexOf("n") !== -1) h = h0 - dy;
      var corner = dir.length === 2;
      if (corner && (meta.ratio ? !ev.shiftKey : ev.shiftKey)) {
        if (Math.abs(dx) >= Math.abs(dy)) h = w / ratio; else w = h * ratio;
      }
      w = Math.max(8, Math.round(w)); h = Math.max(8, Math.round(h));
      x = dir.indexOf("w") !== -1 ? Math.round(x0 + (w0 - w)) : Math.round(x0);
      y = dir.indexOf("n") !== -1 ? Math.round(y0 + (h0 - h)) : Math.round(y0);
      apply();
    }
    function end() {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);
      root.classList.remove("hwdi-dz-dragging");
      post("change", { blockId: idOf(el), values: { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) } });
    }
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", end, true);
    window.addEventListener("pointercancel", end, true);
  }

  // Where a block is now, relative to its container: used to switch it to
  // free position without moving it.
  function measure(el) {
    var parentBox = container(el);
    var c = parentBox.getBoundingClientRect();
    var r = visualRect(el);
    // Hidden frame (nothing laid out): no measure rather than zeros.
    if (!r.width && !r.height) return {};
    return {
      x: Math.round(r.left - (c.left + parentBox.clientLeft)),
      y: Math.round(r.top - (c.top + parentBox.clientTop)),
      w: Math.ceil(r.width) + 1,
      h: Math.ceil(r.height),
    };
  }

  function build() {
    var style = document.createElement("style");
    style.setAttribute("data-hwdi-designer", "");
    style.textContent = C.css;
    (document.head || root).appendChild(style);
    hoverBox = document.createElement("div");
    hoverBox.id = "hwdi-dz-hover";
    box = document.createElement("div");
    box.id = "hwdi-dz-box";
    label = document.createElement("div");
    label.id = "hwdi-dz-label";
    box.appendChild(label);
    if (C.edit) {
      ["nw", "n", "ne", "e", "se", "s", "sw", "w"].forEach(function (dir) {
        var handle = document.createElement("div");
        handle.className = "hwdi-dz-h";
        handle.setAttribute("data-dir", dir);
        box.appendChild(handle);
      });
      root.classList.add("hwdi-dz-edit");
    }
    // The handles of a block touching the edge must not widen the page.
    root.classList.add("hwdi-dz");
    root.appendChild(hoverBox);
    root.appendChild(box);
    refresh();
  }

  if (C.edit) {
    document.addEventListener("pointerdown", function (e) {
      if (e.button !== 0) return;
      var handle = e.target && e.target.closest ? e.target.closest(".hwdi-dz-h") : null;
      if (handle && selected) {
        if (info(selected).locked) return;
        e.preventDefault();
        e.stopPropagation();
        takeFocus();
        startResize(e, handle.getAttribute("data-dir"));
        return;
      }
      var el = wrapOf(e.target);
      if (!el) { select(null, true); return; }
      if (e.altKey) { var up = wrapOf(el.parentElement); if (up) el = up; }
      e.preventDefault();
      // preventDefault also cancels the focus a click gives: take it, so the
      // arrow keys reach the preview.
      takeFocus();
      if (el !== selected) select(el, true);
      if (info(el).locked) return;
      startMove(e, el);
    }, true);
    // In edit mode a click selects: no link, button or form control reacts.
    document.addEventListener("click", function (e) {
      if (wrapOf(e.target) || isOurs(e.target)) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    document.addEventListener("dragstart", function (e) { e.preventDefault(); }, true);
    document.addEventListener("pointermove", function (e) {
      if (!hoverBox || root.classList.contains("hwdi-dz-dragging")) return;
      var el = wrapOf(e.target);
      if (!el || el === selected) { hoverBox.style.display = "none"; return; }
      place(hoverBox, visualRect(el));
    }, true);
    document.addEventListener("keydown", function (e) {
      var mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z" || e.key === "y")) {
        e.preventDefault();
        post(e.key === "y" || e.shiftKey ? "redo" : "undo", {});
        return;
      }
      if (e.key === "Escape") { select(null, true); return; }
      if (!selected) return;
      if (info(selected).locked) return;
      var step = e.shiftKey ? 10 : 1;
      var dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
      var dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
      if (!dx && !dy) return;
      e.preventDefault();
      if (!nudge || nudge.el !== selected) {
        var start = offsets(selected);
        nudge = { el: selected, x: start.x, y: start.y };
        if (!start.positioned) selected.style.position = "relative";
      }
      nudge.x += dx; nudge.y += dy;
      selected.style.left = nudge.x + "px";
      selected.style.top = nudge.y + "px";
      refresh();
      clearTimeout(nudgeTimer);
      nudgeTimer = setTimeout(function () {
        if (nudge) post("change", { blockId: idOf(nudge.el), values: { x: nudge.x, y: nudge.y } });
        nudge = null;
      }, 450);
    }, true);
  }

  window.addEventListener("message", function (e) {
    var d = e.data;
    if (!d || d.source !== C.parent) return;
    if (d.action === "designer:select") select(byId(d.blockId), false, !!d.reveal);
    else if (d.action === "designer:focus") takeFocus();
    else if (d.action === "designer:measure") {
      var el = byId(d.blockId);
      if (el) post("measure", { blockId: d.blockId, values: measure(el), request: d.request || "" });
    }
  });

  function watch() {
    if (window.ResizeObserver) new ResizeObserver(refresh).observe(document.documentElement);
    window.addEventListener("resize", refresh);
    window.addEventListener("load", refresh);
    document.addEventListener("load", refresh, true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { build(); watch(); });
  else { build(); watch(); }
};

/** The <script> added to the preview frame's head (editor only). */
export const buildDesignerScript = ({ source, parentSource, edit = true, selectedId = null, blocks = {} }) => {
  const config = {
    source,
    parent: parentSource,
    kind: DESIGNER_MESSAGE_KIND,
    edit: !!edit,
    selectedId: SAFE_ID.test(String(selectedId || "")) ? selectedId : null,
    blocks,
    css: DESIGNER_CSS,
  };
  const json = JSON.stringify(config).replace(/</g, "\\u003c");
  return `<script data-hwdi-designer>(${designerRuntime.toString()})(${json});</script>`;
};
